import { alpha, Components, Theme } from '@mui/material/styles';
import palette from './palette';
import { brandColors, interactions } from './colors';

const BORDER_RADIUS = 8;

/**
 * Create component overrides for a specific theme mode
 */
export const createComponentOverrides = (
  mode: 'light' | 'dark'
): Components<Theme> => {
  const isDark = mode === 'dark';

  // Theme-specific colors
  const colors = {
    text: {
      primary: isDark ? palette.purple[100] : palette.gray[900],
      secondary: isDark ? palette.purple[200] : palette.gray[600],
    },
    background: {
      input: isDark ? palette.purple[900] : palette.white,
      inputHover: isDark ? palette.purple[700] : palette.gray[50],
      card: isDark ? palette.purple[700] : palette.white,
      dropdown: isDark ? palette.purple[900] : palette.white,
    },
    border: {
      default: isDark ? palette.purple[950] : palette.gray[200],
      input: isDark ? palette.purple[900] : palette.gray[300],
      inputHover: isDark ? palette.purple[700] : palette.gray[400],
      inputFocus: isDark ? palette.purple[300] : brandColors.primary,
    },
  };

  // Components are organized alphabetically for easy navigation
  return {
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiAutocomplete-inputRoot': {
            color: colors.text.primary,
          },
          '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator':
            {
              color: colors.text.primary,
            },
        },
        paper: {
          backgroundColor: colors.background.dropdown,
          border: `1px solid ${colors.border.input}`,
          borderRadius: BORDER_RADIUS,
        },
        option: {
          padding: '12px 16px',
          backgroundColor: colors.background.dropdown,
          color: colors.text.primary,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isDark ? palette.purple[600] : palette.gray[100],
          },
          '&.Mui-focused': {
            backgroundColor: isDark ? palette.purple[600] : palette.gray[100],
          },
          '&[aria-selected="true"]': {
            backgroundColor: isDark ? palette.purple[700] : palette.purple[100],
            color: isDark ? palette.purple[50] : brandColors.primary,
            '&:hover': {
              backgroundColor: isDark
                ? palette.purple[600]
                : palette.purple[200],
            },
          },
        },
        noOptions: {
          padding: '12px 16px',
          fontSize: '0.875rem',
          backgroundColor: colors.background.dropdown,
          color: isDark ? palette.purple[200] : palette.gray[600],
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: BORDER_RADIUS,
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          backgroundColor: brandColors.primary,
          '&:hover': {
            backgroundColor: isDark ? palette.purple[400] : palette.purple[600],
            boxShadow: `0 4px 12px ${palette.purple[800]}`,
          },
          '&:disabled': {
            backgroundColor: isDark ? palette.purple[650] : palette.gray[200],
            color: isDark ? alpha(palette.purple[100], 0.5) : palette.gray[400],
          },
        },
        outlined: {
          borderColor: isDark ? palette.purple[300] : brandColors.primary,
          color: colors.text.primary,
          '&:hover': {
            backgroundColor: interactions.primary.hover,
            borderColor: isDark ? palette.purple[300] : brandColors.primary,
          },
        },
        text: {
          color: colors.text.primary,
          '&:hover': {
            backgroundColor: interactions.primary.hover,
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.card,
          border: `1px solid ${colors.border.default}`,
          borderRadius: BORDER_RADIUS,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease-in-out',
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
          backgroundColor: colors.background.input,
          borderRadius: BORDER_RADIUS,
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
          fontSize: '0.875rem', // 14px - matching inputLabel variant
          fontWeight: 500,
          lineHeight: 1.4,
          marginBottom: 0,
          position: 'static',
          transform: 'none',
          '&.MuiInputLabel-shrink': {
            color: colors.text.primary,
          },
          '&.Mui-focused': {
            color: colors.text.primary,
          },
          '&.Mui-error': {
            color: '#ef4444', // Direct error color that works in both themes
          },
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS,
          backgroundColor: isDark ? palette.purple[750] : palette.gray[200],
        },
        bar: {
          borderRadius: BORDER_RADIUS,
          backgroundColor: brandColors.primary,
        },
      },
    },

    MuiModal: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(8px)',
          backgroundColor: isDark ? palette.purple[950] : palette.gray[50],
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.border.input,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.border.inputHover,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.border.inputFocus,
            borderWidth: '2px',
          },
        },
      },
    },

    MuiSlider: {
      styleOverrides: {
        root: {
          height: 8,
          padding: '0 !important',
          color: isDark ? palette.purple[600] : brandColors.primary,
        },
        track: {
          border: 'none',
          background: `linear-gradient(to right, ${isDark ? palette.purple[600] : brandColors.primary}, ${isDark ? palette.purple[600] : brandColors.primary})`,
        },
        rail: {
          opacity: 1,
          backgroundColor: isDark ? palette.purple[900] : palette.purple[200],
        },
        thumb: {
          height: 20,
          width: 20,
          backgroundColor: isDark ? palette.purple[600] : brandColors.primary,
          border: '2px solid white',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          '&:before': {
            display: 'none',
          },
          '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
            boxShadow: `0 2px 12px ${isDark ? palette.purple[800] : palette.purple[700]}`,
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: isDark ? palette.purple[800] : palette.purple[600],
          color: palette.purple[50],
          borderRadius: BORDER_RADIUS,
          border: `1px solid ${isDark ? palette.purple[600] : palette.purple[300]}`,
          boxShadow: `0 8px 24px ${isDark ? palette.purple[950] : palette.purple[800]}`,
          fontSize: '0.875rem',
          fontWeight: 500,
          padding: '12px 16px',
          maxWidth: 320,
          backdropFilter: 'blur(8px)',
          background: isDark
            ? `linear-gradient(135deg, ${palette.purple[800]}, ${palette.purple[700]})`
            : `linear-gradient(135deg, ${palette.purple[600]}, ${palette.purple[500]})`,
        },
        arrow: {
          color: isDark ? palette.purple[800] : palette.purple[600],
          '&::before': {
            border: `1px solid ${isDark ? palette.purple[600] : palette.purple[300]}`,
            backgroundColor: isDark
              ? `linear-gradient(135deg, ${palette.purple[800]}, ${palette.purple[700]})`
              : `linear-gradient(135deg, ${palette.purple[600]}, ${palette.purple[500]})`,
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: colors.background.input,
            borderRadius: BORDER_RADIUS,
            '&:hover': {
              backgroundColor: colors.background.inputHover,
            },
            '&.Mui-focused': {
              backgroundColor: isDark ? palette.purple[800] : palette.white,
            },
            // Fix webkit autofill styling to be theme-appropriate
            '& input:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 30px ${colors.background.input} inset !important`,
              WebkitTextFillColor: `${colors.text.primary} !important`,
              transition: 'background-color 5000s ease-in-out 0s',
            },
            '& input:-webkit-autofill:hover': {
              WebkitBoxShadow: `0 0 0 30px ${colors.background.inputHover} inset !important`,
              WebkitTextFillColor: `${colors.text.primary} !important`,
            },
            '& input:-webkit-autofill:focus': {
              WebkitBoxShadow: `0 0 0 30px ${isDark ? palette.purple[800] : palette.white} inset !important`,
              WebkitTextFillColor: `${colors.text.primary} !important`,
            },
            '& input:-webkit-autofill:active': {
              WebkitBoxShadow: `0 0 0 30px ${colors.background.input} inset !important`,
              WebkitTextFillColor: `${colors.text.primary} !important`,
            },
          },
        },
      },
    },

    // Date picker specific overrides for dark theme
    ...(isDark && {
      MuiDialog: {
        styleOverrides: {
          paper: {
            '&[role="dialog"]': {
              backgroundColor: palette.purple[800],
              backgroundImage: 'none',
              border: `1px solid ${palette.purple[600]}`,
              borderRadius: `${BORDER_RADIUS}px`,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            '&.MuiPickersLayout-root, &.MuiPickersPopper-paper': {
              backgroundColor: `${palette.purple[800]} !important`,
              backgroundImage: 'none !important',
              border: `1px solid ${palette.purple[600]}`,
              borderRadius: `${BORDER_RADIUS}px !important`,
            },
          },
        },
      },

      MuiTypography: {
        styleOverrides: {
          // Specific variants for consistent styling
          h1: { color: colors.text.primary },
          h2: { color: colors.text.primary },
          h3: { color: colors.text.primary },
          h4: { color: colors.text.primary },
          h5: { color: colors.text.primary },
          h6: { color: colors.text.primary },
          body1: { color: colors.text.primary },
          body2: { color: colors.text.secondary },
          subtitle1: { color: colors.text.secondary },
          subtitle2: { color: colors.text.secondary },
          caption: { color: colors.text.secondary },
        },
      },
    }),
  };
};
